import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

export default function CaseStudiesPage() {
  const caseStudies = [
    {
      appName: 'FinanceFlow',
      category: 'Fintech',
      challenge: 'Low conversion rate on onboarding flow',
      crownScoreBefore: 58,
      crownScoreAfter: 82,
      results: [
        '+42% onboarding completion rate',
        '+28% 7-day retention',
        'Secured $2.5M seed round',
        'Reduced critical bugs by 89%'
      ],
      testimonial: "CrowdRounds identified the exact friction points killing our conversions. After implementing their recommendations, we saw immediate improvement and investors took us seriously.",
      founder: 'Sarah Chen',
      title: 'CEO, FinanceFlow',
      timeline: '8 weeks from Round 1 to funding',
      image: 'https://public.readdy.ai/ai/img_res/534c9b3998ae6f041244d17fa140dc73.jpg'
    },
    {
      appName: 'FitTrack Pro',
      category: 'Health & Fitness',
      challenge: 'High churn rate after first week',
      crownScoreBefore: 64,
      crownScoreAfter: 88,
      results: [
        '+56% 30-day retention',
        '+34% feature adoption',
        'App Store featured placement',
        '4.8★ rating (up from 3.9★)'
      ],
      testimonial: "The behavioral insights from CrowdRounds were game-changing. We discovered users didn't understand our core value prop. Three weeks after fixing it, retention doubled.",
      founder: 'Marcus Johnson',
      title: 'Founder, FitTrack Pro',
      timeline: '12 weeks from Round 1 to App Store feature',
      image: 'https://readdy.ai/api/search-image?query=fitness%20tracking%20mobile%20app%20showing%20workout%20progress%20charts%20exercise%20routines%20and%20health%20metrics%20on%20modern%20smartphone%20vibrant%20energetic%20design%20green%20and%20orange%20accents&width=800&height=600&seq=fittrack-case&orientation=landscape'
    },
    {
      appName: 'TaskMaster',
      category: 'Productivity',
      challenge: 'Investors wanted validation data before Series A',
      crownScoreBefore: 71,
      crownScoreAfter: 91,
      results: [
        'Closed $8M Series A',
        '+19% task completion rate',
        'Reduced support tickets by 67%',
        'Expanded to enterprise market'
      ],
      testimonial: "Our investors required third-party validation before committing to Series A. CrowdRounds delivered investor-grade data that closed the deal. Worth every penny.",
      founder: 'Emily Rodriguez',
      title: 'Co-Founder, TaskMaster',
      timeline: '6 weeks from Round 3 to Series A close',
      image: 'https://readdy.ai/api/search-image?query=productivity%20task%20management%20app%20interface%20with%20organized%20to-do%20lists%20project%20boards%20and%20calendar%20view%20on%20tablet%20device%20clean%20professional%20purple%20and%20gray%20design&width=800&height=600&seq=taskmaster-case&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-24">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Success Stories
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Real founders, real results — see how CrowdRounds validation helped apps improve metrics, secure funding, and achieve product-market fit
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-24">
            {caseStudies.map((study, index) => (
              <div key={index} className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
                    {study.category}
                  </div>
                  <h2 className="text-4xl font-bold text-slate-900 mb-4">{study.appName}</h2>
                  <p className="text-xl text-gray-700 mb-8">{study.challenge}</p>

                  {/* CrownScore Improvement */}
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 mb-8">
                    <div className="text-sm text-gray-600 mb-4">CrownScore Improvement</div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-gray-400 mb-1">{study.crownScoreBefore}</div>
                        <div className="text-xs text-gray-600">Before</div>
                      </div>
                      <i className="ri-arrow-right-line text-3xl text-blue-600"></i>
                      <div className="text-center">
                        <div className="text-4xl font-bold text-blue-600 mb-1">{study.crownScoreAfter}</div>
                        <div className="text-xs text-gray-600">After</div>
                      </div>
                      <div className="ml-auto">
                        <div className="text-3xl font-bold text-green-600">+{study.crownScoreAfter - study.crownScoreBefore}</div>
                        <div className="text-xs text-gray-600">points</div>
                      </div>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-slate-900 mb-4">Key Results</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <i className="ri-check-circle-fill text-green-600 text-xl mt-0.5 flex-shrink-0"></i>
                          <span className="text-gray-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Testimonial */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <i className="ri-double-quotes-l text-4xl text-blue-600 mb-4 block"></i>
                    <p className="text-gray-700 italic mb-4 leading-relaxed">{study.testimonial}</p>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {study.founder.charAt(0)}
                      </div>
                      <div>
                        <div className="font-bold text-slate-900">{study.founder}</div>
                        <div className="text-sm text-gray-600">{study.title}</div>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="mt-6 flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-time-line"></i>
                    <span>{study.timeline}</span>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <img 
                    src={study.image}
                    alt={study.appName}
                    className="w-full h-auto rounded-2xl shadow-2xl"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* By The Numbers */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-white text-center mb-16">Aggregate Impact Across All Case Studies</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">+42%</div>
              <div className="text-xl text-slate-300">Avg. Retention Lift</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">+31%</div>
              <div className="text-xl text-slate-300">Avg. Conversion Increase</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">$13M+</div>
              <div className="text-xl text-slate-300">Total Funding Raised</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">+26</div>
              <div className="text-xl text-slate-300">Avg. CrownScore Gain</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Ready to Write Your Success Story?</h2>
          <p className="text-xl text-gray-700 mb-8">
            Start your validation journey today and join the founders who've transformed their apps with CrowdRounds
          </p>
          <a
            href="/client-portal/create-round"
            className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all whitespace-nowrap cursor-pointer"
          >
            Start Your First Test
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
